import { faDigits } from '../../lib/numerals'
import { MONTHS_FA, dayToCol } from './constants'
import { isoToDateKey, makeDayKey } from './dates'

// ─── The publishing calendar's pure half ──────────────────────────────────────
//
// A Gregorian month grid, Monday-first (owner decision, 2026-10-13; it was a
// Jalali grid before). `key` is the `YYYY-MM-DD` day the lessons are bucketed
// by, `day` is what is drawn. `scheduledPublishAt` is a UTC instant on the
// wire (backend-nest ADR-0008); the grid works in the browser's local day.

/** Column order of a Monday-first week, full names. */
export const WEEKDAY_NAMES_FA = ['دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه', 'شنبه', 'یکشنبه']

/** Columns of the weekend (Saturday, Sunday), drawn muted. */
export const WEEKEND_COLS = [5, 6]

/** The `{ year, month }` (1-based month) a Date falls in, local day. */
export function monthOf(date) {
  return { year: date.getFullYear(), month: date.getMonth() + 1 }
}

/** `delta` months from a month (1-based), across year ends. */
export function shiftMonth({ year, month }, delta) {
  const index = year * 12 + (month - 1) + delta
  return { year: Math.floor(index / 12), month: (index % 12) + 1 }
}

/** `اکتبر`; digits are the caller's to localise. */
export function monthName(month) {
  return MONTHS_FA[month - 1]
}

/**
 * The grid for one month: Monday-first, `null` padding before the 1st, then
 * one cell per day with its day key and weekday column.
 */
export function monthCells(year, month) {
  const startCol = dayToCol[new Date(year, month - 1, 1).getDay()]
  const cells = Array.from({ length: startCol }, () => null)
  const length = new Date(year, month, 0).getDate()
  for (let day = 1; day <= length; day += 1) {
    cells.push({ day, key: makeDayKey(year, month - 1, day), col: (startCol + day - 1) % 7 })
  }
  return cells
}

/** `YYYY-MM-DD` → its `{ year, month, day }` and weekday column (0 = Monday). */
export function dayKeyParts(dayKey) {
  const [year, month, day] = dayKey.split('-').map(Number)
  return { year, month, day, col: dayToCol[new Date(year, month - 1, day).getDay()] }
}

/** `دوشنبه ۱۳ اکتبر ۲۰۲۶` for a `YYYY-MM-DD` day key. */
export function dayLabel(dayKey) {
  const d = dayKeyParts(dayKey)
  return `${WEEKDAY_NAMES_FA[d.col]} ${faDigits(d.day)} ${MONTHS_FA[d.month - 1]} ${faDigits(d.year)}`
}

/** Same day as `dayKey` at `time` (`HH:MM`); a lesson already scheduled keeps its own time unless one is passed. */
export function retimeToDay(dayKey, existingIso, time) {
  const [y, m, d] = dayKey.split('-').map(Number)
  let hours = 9
  let minutes = 0
  if (time) [hours, minutes] = time.split(':').map(Number)
  else if (existingIso) {
    const prior = new Date(existingIso)
    hours = prior.getHours()
    minutes = prior.getMinutes()
  }
  return new Date(y, m - 1, d, hours, minutes).toISOString()
}

/** `HH:MM` of an instant in the browser's local time. */
export function timeOfDay(iso) {
  const d = new Date(iso)
  const pad = n => String(n).padStart(2, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/**
 * Why a draft cannot be published yet, or `null` when it can: still being
 * auto-drafted, no sentences, or sentences without reference audio (the
 * server refuses those too — `IncompleteLessonError`).
 */
export function publishBlocker(lesson) {
  if (lesson.status !== 'draft') return null
  if (lesson.processingStatus) return 'هنوز در حال پردازش یا در انتظار تأیید است'
  if (!lesson.sentences?.length) return 'جمله‌ای ندارد'
  // A one-sentence lesson has a video or an audio (2026-10-13); a video's
  // sound is its audio, so one uploaded before then needs a re-upload.
  if (lesson.sentences.length === 1 && !lesson.sentences[0].referenceAudioUrl) {
    return lesson.videoUrl ? 'صدای ویدیو برداشته نشده؛ ویدیو را دوباره آپلود کنید' : 'ویدیو یا صدا هنوز آپلود نشده'
  }
  const missing = lesson.sentences.filter(s => !s.referenceAudioUrl).length
  return missing > 0 ? `صدای مرجع ${faDigits(missing)} جمله کم است` : null
}

/**
 * Where a lesson stands on the calendar:
 * `published` / `archived` as stored; a draft is `overdue` once its planned
 * time has passed and it is still a draft — the five-minute auto-publish job
 * (`scheduled-publish.service.ts`) skips drafts that fail the publish checks —
 * `scheduled` before that, `unscheduled` with no date at all.
 */
export function lessonPhase(lesson, now = new Date()) {
  if (lesson.status === 'published') return 'published'
  if (lesson.status === 'archived') return 'archived'
  if (!lesson.scheduledPublishAt) return 'unscheduled'
  return new Date(lesson.scheduledPublishAt) < now ? 'overdue' : 'scheduled'
}

export const PHASE_FA = {
  published: 'منتشرشده',
  archived: 'بایگانی',
  overdue: 'عقب‌افتاده',
  scheduled: 'زمان‌بندی‌شده',
  unscheduled: 'بدون تاریخ',
}

/** The dot a day cell shows instead of a title chip on a phone. */
export const PHASE_DOT_CLS = {
  published: 'bg-good',
  archived: 'bg-ink-3',
  overdue: 'bg-bad',
  scheduled: 'bg-warn',
  unscheduled: 'bg-ink-3',
}

export const PHASE_CHIP_CLS = {
  published: 'bg-good-tint text-good-ink',
  archived: 'bg-muted-tint text-ink-3 line-through',
  overdue: 'bg-bad-tint text-bad-ink',
  scheduled: 'bg-warn-tint text-warn-ink',
  unscheduled: 'bg-muted-tint text-ink-2',
}

/**
 * Lessons that survive the filters, bucketed by the Gregorian day they are
 * scheduled on. `status` and `level` are `'all'` or a value.
 */
export function bucketByDay(lessons, { status = 'all', level = 'all' } = {}) {
  const byDay = {}
  for (const l of lessons) {
    if (!l.scheduledPublishAt) continue
    if (status !== 'all' && l.status !== status) continue
    if (level !== 'all' && l.level !== level) continue
    const k = isoToDateKey(l.scheduledPublishAt)
    ;(byDay[k] ??= []).push(l)
  }
  for (const k of Object.keys(byDay)) {
    byDay[k].sort((a, b) => new Date(a.scheduledPublishAt) - new Date(b.scheduledPublishAt))
  }
  return byDay
}

/** Counts for the month strip, over the cells actually drawn. */
export function monthSummary(cells, byDay, now = new Date()) {
  const out = { scheduled: 0, published: 0, overdue: 0 }
  for (const cell of cells) {
    if (!cell) continue
    for (const l of byDay[cell.key] ?? []) {
      const phase = lessonPhase(l, now)
      if (phase === 'published') out.published += 1
      else if (phase === 'overdue') out.overdue += 1
      else if (phase === 'scheduled') out.scheduled += 1
    }
  }
  return out
}
